import { useEffect } from "react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import {
  isRouteErrorResponse,
  json,
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteLoaderData,
  useRouteError,
} from "@remix-run/react";
import { Toaster } from "sonner";
import "./sonner.css";
import stylesheet from "~/tailwind.css?url";
import type { LinksFunction, MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => {
  return [
    {
      title: "Power to Live | Registration Form",
      description: "Welcome to Power to Live",
    },
  ];
};

export const links: LinksFunction = () => {
  return [
    { rel: "icon", href: "/favicon.png" },
    { rel: "stylesheet", href: stylesheet },
    {
      rel: "stylesheet",
      href: "https://unpkg.com/file-icon-vectors@1.0.0/dist/file-icon-vivid.min.css",
    },
  ];
};

export async function loader() {
  const appUrl = process.env.APP_URL;
  const gtmContainerId = process.env.GTM_CONTAINER_ID || undefined;

  return json({
    ENV: {
      MAPBOX_ACCESS_TOKEN: process.env.MAPBOX_ACCESS_TOKEN,
      APP_URL: appUrl,
      SENTRY_DSN: process.env.SENTRY_DSN,
      GOOGLE_TAG_ID: process.env.GOOGLE_TAG_ID,
    },
    gtmContainerId,
  });
}

export function Layout({ children }: { children: React.ReactNode }) {
  const rootData = useRouteLoaderData<typeof loader>("root");
  const tagId = rootData?.ENV.GOOGLE_TAG_ID;
  const gtmContainerId = rootData?.gtmContainerId;

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {gtmContainerId ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer',${JSON.stringify(gtmContainerId)});
              `.trim(),
            }}
          />
        ) : null}
        <Meta />
        <Links />
        {tagId ? (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${tagId}`}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){window.dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', ${JSON.stringify(tagId)}, { send_page_view: false });
                `.trim(),
              }}
            />
          </>
        ) : null}
      </head>
      <body>
        {gtmContainerId ? (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmContainerId}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
              title="Google Tag Manager"
            />
          </noscript>
        ) : null}
        <Toaster position="top-center" richColors />
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);

  let message = {
    title: "Oops! Something went wrong.",
    description: "We're working on fixing this issue. Please try again later.",
  };

  if (isRouteErrorResponse(error) && error.status === 404) {
    message = {
      title: "404 | Not Found",
      description: "The page you're looking for doesn't exist.",
    };
  }

  if (isRouteErrorResponse(error) && error.status === 500) {
    message = {
      title: "500 | Server Error",
      description: "Something went wrong on our end. Please try again later.",
    };
  }

  return (
    <div className="min-h-screen  flex flex-col items-center justify-center bg-gray-100 p-4">
      <div>
        <h1 className="text-xl font-medium text-gray-500 mb-4">
          {message.title}
        </h1>
        <p className="text-gray-500 mb-8">{message.description}</p>
        <Link to="/" className="text-blue-500">
          Go back home
        </Link>
      </div>
    </div>
  );
};

export default function App() {
  const { ENV } = useLoaderData<typeof loader>();
  const location = useLocation();

  useEffect(() => {
    if (!ENV.GOOGLE_TAG_ID) {
      return;
    }

    window.gtag?.("event", "page_view", {
      page_path: `${location.pathname}${location.search}${location.hash}`,
      page_location: window.location.href,
    });
  }, [ENV.GOOGLE_TAG_ID, location.hash, location.pathname, location.search]);

  return (
    <>
      <Outlet />
      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV = ${JSON.stringify(ENV)}`,
        }}
      />
    </>
  );
}
